import styled from "styled-components"
import { BrowserRouter as Router, Link } from "react-router-dom"
import { Code, Palette, Database, Terminal, Server, TrendingUp, Users, Star, Clock } from "lucide-react"

import postgreSqlImg from '../../resource/PostgreSql.png';
import reactImg from '../../resource/react.png';
import pythonImg from '../../resource/python.png';
import amazonImg from '../../resource/amazon.png';
import figmaImg from '../../resource/figma.png';
import mongodbImg from '../../resource/mongo.png';
import dockerImg from '../../resource/docker.png';
import uiuxImg from '../../resource/uxui.png';

const Main = styled.main`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 2rem 0;
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`

const CategoryCard = styled(Link)`
  background: ${(props) => props.bgcolor || "#fff"};
  color: ${(props) => props.color || "#000"};
  padding: 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);

  &:hover {
    transform: translateY(-2px);
  }
`

const CategoryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CategoryTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`

const CategoryCount = styled.span`
  font-size: 0.875rem;
  opacity: 0.8;
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const CourseCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`

const CourseImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`

const CourseContent = styled.div`
  padding: 1rem;
`

const CourseCategory = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  background: ${(props) => props.bgcolor || "#e2e8f0"};
  color: ${(props) => props.color || "#1e293b"};
`

const CourseTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0.5rem 0;
`

const CoursePrice = styled.div`
  font-weight: 600;
  color: #0f172a;
`

const CourseStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.5rem;
`

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  width: 100%;
  border-bottom: 1px solid #e2e8f0;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
`

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  height: 4rem;
  align-items: center;
  padding: 0 1rem;
`

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  margin-right: 2rem;
  color: #0f172a;
  text-decoration: none;
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`

const NavLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
  opacity: ${(props) => (props.active ? 1 : 0.6)};
  transition: opacity 0.2s;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
`

function Header() {
  const navigation = [
    { name: "Programming", href: "/Programming" },
    { name: "Graphics", href: "/Graphics" },
    { name: "Database", href: "/Database" },
    { name: "DevOps", href: "/Devops" },
    { name: "Infra", href: "/Infra" },
  ]

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">AntsNest</Logo>
        <Nav>
          {navigation.map((item) => (
            <NavLink key={item.href} to={item.href}>
              {item.name}
            </NavLink>
          ))}
        </Nav>
        <div style={{ marginLeft: "auto", display: "flex", gap: "1rem" }}>
          <IconButton as={Link} to="/cart">
            <Users size={20} />
          </IconButton>
          <IconButton as={Link} to="/auth">
            <Star size={20} />
          </IconButton>
        </div>
      </HeaderContent>
    </HeaderContainer>
  )
}

function Store() {
  const categories = [
    {
      name: "프로그래밍",
      icon: Code,
      count: 150,
      bgcolor: "#3b82f6",
      color: "white",
      href: "/course-library/1",
    },
    {
      name: "그래픽",
      icon: Palette,
      count: 89,
      bgcolor: "#ef4444",
      color: "white",
      href: "/course-library/2",
    },
    {
      name: "데이터베이스",
      icon: Database,
      count: 67,
      bgcolor: "#10b981",
      color: "white",
      href: "/course-library/3",
    },
    {
      name: "데브옵스",
      icon: Terminal,
      count: 45,
      bgcolor: "#8b5cf6",
      color: "white",
      href: "/course-library/4",
    },
    {
      name: "인프라",
      icon: Server,
      count: 34,
      bgcolor: "#f59e0b",
      color: "white",
      href: "/course-library/5",
    },
  ]

  const newCourses = [
    {
      id: 1,
      title: "React 완벽 가이드",
      category: "프로그래밍",
      price: 49000,
      image: reactImg,
      students: 1234,
      rating: 4.8,
      categoryColor: "#3b82f6",
    },
    {
      id: 2,
      title: "Figma 마스터 클래스",
      category: "그래픽",
      price: 39000,
      image: figmaImg,
      students: 856,
      rating: 4.9,
      categoryColor: "#ef4444",
    },
    {
      id: 3,
      title: "MongoDB 실전 활용",
      category: "데이터베이스",
      price: 45000,
      image: mongodbImg,
      students: 645,
      rating: 4.7,
      categoryColor: "#10b981",
    },
    {
      id: 4,
      title: "Docker & Kubernetes",
      category: "데브옵스",
      price: 55000,
      image: dockerImg,
      students: 923,
      rating: 4.9,
      categoryColor: "#8b5cf6",
    },
  ]

  const popularCourses = [
    {
      id: 5,
      title: "Python 기초부터 실전까지",
      category: "프로그래밍",
      price: 35000,
      image: pythonImg,
      students: 2341,
      rating: 4.9,
      categoryColor: "#3b82f6",
    },
    {
      id: 6,
      title: "AWS 클라우드 아키텍처",
      category: "인프라",
      price: 59000,
      image: amazonImg,
      students: 1567,
      rating: 4.8,
      categoryColor: "#f59e0b",
    },
    {
      id: 7,
      title: "UI/UX 디자인 원칙",
      category: "그래픽",
      price: 42000,
      image: uiuxImg,
      students: 1890,
      rating: 4.7,
      categoryColor: "#ef4444",
    },
    {
      id: 8,
      title: "PostgreSQL 성능 최적화",
      category: "데이터베이스",
      price: 48000,
      image: postgreSqlImg,
      students: 1234,
      rating: 4.8,
      categoryColor: "#10b981",
    },
  ]

  return (
      <Main>
        <Container>
          <CategoryGrid>
            {categories.map((category) => (
              <CategoryCard key={category.name} to={category.href} bgcolor={category.bgcolor} color={category.color}>
                <CategoryIcon>
                  <category.icon size={24} />
                </CategoryIcon>
                <CategoryTitle>{category.name}</CategoryTitle>
                <CategoryCount>{category.count}개 강좌</CategoryCount>
              </CategoryCard>
            ))}
          </CategoryGrid>

          <SectionTitle>
            <TrendingUp size={24} />
            신규 강좌
          </SectionTitle>
          <CourseGrid>
            {newCourses.map((course) => (
              <CourseCard key={course.id}>
                <CourseImage src={course.image} alt={course.title} />
                <CourseContent>
                  <CourseCategory bgcolor={course.categoryColor} color="white">
                    {course.category}
                  </CourseCategory>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CoursePrice>{course.price.toLocaleString()}원</CoursePrice>
                  <CourseStats>
                    <StatItem>
                      <Users size={16} />
                      {course.students.toLocaleString()}
                    </StatItem>
                    <StatItem>
                      <Star size={16} />
                      {course.rating}
                    </StatItem>
                    <StatItem>
                      <Clock size={16} />
                      New
                    </StatItem>
                  </CourseStats>
                </CourseContent>
              </CourseCard>
            ))}
          </CourseGrid>

          <SectionTitle>
            <Star size={24} />
            인기 강좌
          </SectionTitle>
          <CourseGrid>
            {popularCourses.map((course) => (
              <CourseCard key={course.id}>
                <CourseImage src={course.image} alt={course.title} />
                <CourseContent>
                  <CourseCategory bgcolor={course.categoryColor} color="white">
                    {course.category}
                  </CourseCategory>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CoursePrice>{course.price.toLocaleString()}원</CoursePrice>
                  <CourseStats>
                    <StatItem>
                      <Users size={16} />
                      {course.students.toLocaleString()}
                    </StatItem>
                    <StatItem>
                      <Star size={16} />
                      {course.rating}
                    </StatItem>
                  </CourseStats>
                </CourseContent>
              </CourseCard>
            ))}
          </CourseGrid>
        </Container>
      </Main>
  )
}

export default Store

